import { LocalizedEnum } from '../../../api/journodex/model/localized-enum';
import { CirculationApiModel, OutletApiModel } from '../../../api/targeting/model/targeting-search-result.api.model';
import { CirculationType, CirculationModel } from './circulation.model';
import { BaseJournodexEntityModel, FRESHNESS_THRESHOLD_MS, VETERAN_THRESHOLD_MS } from './base-journodex-entity.model';
import { EntityType } from '../../../api/targeting/enum/entity.type.enum';
import { TargetingMediaTypeEnum } from '../enums/targeting-media-type.enum';
import { MediaMarket } from '../../Core/models/media-market.model';
import { LocalizedEnumApiModel } from '../../../api/journodex/model/localized-enum-api.model';
import { HandleModel } from './handle.model';
import { LivenessEnum } from '../../../api/targeting/enum/liveness.enum';
import { SubjectUtils } from '../../../Common/Journodex/services/journodex-subjects.service';
import { HandleTypeEnum } from '../../../api/targeting/enum/handle-type.enum';

export class OutletModel extends BaseJournodexEntityModel {

    name?: string;
    productType?: LocalizedEnum;
    productFrequency?: LocalizedEnum;
    marketSector?: LocalizedEnum;
    targetAudiences?: LocalizedEnum[];
    areasOfCoverage?: number[];
    circulations?: CirculationModel[];
    mediaTypes?: LocalizedEnum[];
    mediaMarkets?: MediaMarket[];
    liveness?: LivenessEnum;

    uniqueVisitors: CirculationModel;

    parentOutletId?: string;
    domainAuthority: number;
    isAiReference?: boolean;

    // quick search only
    location?: string;

    constructor(apiModel?: OutletApiModel, locale?: string) {
        super(apiModel, locale);
        this.entityType = EntityType.OUTLET;
        if (apiModel) {
            this.name = apiModel.name;

            if (apiModel.parentOutlet) {
                this.parentOutletId = apiModel.parentOutlet.id;
            }
            if (apiModel.productType) {
                this.productType = new LocalizedEnum(apiModel.productType);
            }
            if (apiModel.productFrequency) {
                this.productFrequency = new LocalizedEnum(apiModel.productFrequency);
            }
            if (apiModel.marketSector) {
                this.marketSector = new LocalizedEnum(apiModel.marketSector);
            }
            if (apiModel.targetAudiences) {
                this.targetAudiences = apiModel.targetAudiences.map(x => new LocalizedEnum(x));
            }
            if (apiModel.handles) {
                this.handles = apiModel.handles.map(handle => new HandleModel(handle, false, this.entityType));
                const email = this.handles.find(x => x.primary && x.handleType === HandleTypeEnum.EMAIL);
                this.primaryEmail = !!email ? email.value : null;
            }
            if (apiModel.circulation) {
                this.circulations = apiModel.circulation.map(x => new CirculationModel(x));
                this.circulations = this.circulations.filter(
                    circulation => {
                        // Don't show 0s
                        return circulation.value !== 0;
                    }
                );

                if (this.circulations.find(x => x.circulationType.enumValue === CirculationType.manualValue)) {
                    this.uniqueVisitors = this.circulations.find(x => x.circulationType.enumValue === CirculationType.manualValue);
                    this.circulations = this.circulations.filter(x => x.circulationType.enumValue !== CirculationType.partnerValue);
                } else {
                    this.uniqueVisitors = this.circulations.find(x => x.circulationType.enumValue === CirculationType.partnerValue);
                }
            }
            if (apiModel.mediaTypes) {
                this.mediaTypes = apiModel.mediaTypes
                    .filter(x => Object.values(TargetingMediaTypeEnum).includes(x.enumValue as TargetingMediaTypeEnum))
                    .map(x => {
                        x.displayString = `Discover.targeting.filter.mediaType.${x.enumValue as TargetingMediaTypeEnum}`;

                        return new LocalizedEnum(x);
                    });
            }
            if (apiModel.mediaMarkets) {
                this.mediaMarkets = apiModel.mediaMarkets.map(mm => new MediaMarket(mm));
                const filteredMarkets = [];
                this.mediaMarkets.forEach(market => {
                    if (!filteredMarkets.some(filteredMarket => filteredMarket.id === market.id)) {
                        filteredMarkets.push(market);
                    }
                });
                this.mediaMarkets = filteredMarkets;
            }
            if (apiModel.domainAuthority) {
                this.domainAuthority = apiModel.domainAuthority;
            }
            this.liveness = apiModel.liveness;

            if (apiModel.areasOfCoverage) {
                this.areasOfCoverage = apiModel.areasOfCoverage?.map(areaOfCoverage => areaOfCoverage.id);
            }

            this.isAiReference = apiModel.isAiReference;

            this.setBadges(apiModel);
        }
    }

    setBadges(apiModel: OutletApiModel) {
        this.badges = [];

        if ((this.dateDeltaToNow(apiModel.creationDate)) < FRESHNESS_THRESHOLD_MS) {
            this.badges.push({
                iconClass: 'agpr-i-badge-new',
                tooltip: 'Discover.badges.tooltips.' + EntityType.OUTLET.toLowerCase() + '.new'
            });
        }
        if ((this.dateDeltaToNow(apiModel.modifiedDate)) < FRESHNESS_THRESHOLD_MS) {
            this.badges.push({
                iconClass: 'fa-solid fa-sparkle',
                tooltip: 'Discover.badges.tooltips.' + EntityType.OUTLET.toLowerCase() + '.recentlyUpdated'
            });
        }
        if ((this.dateDeltaToNow(apiModel.creationDate)) > VETERAN_THRESHOLD_MS) {
            this.badges.push({
                iconClass: 'fa-kit fa-agpr-ten-more',
                tooltip: 'Discover.badges.tooltips.' + EntityType.OUTLET.toLowerCase() + '.veteran'
            });
        }
        if (apiModel.isAiReference) {
            this.badges.push({
                iconClass: 'fa-kit-duotone fa-agpr-ai-badge',
                tooltip: 'Discover.badges.tooltips.' + EntityType.OUTLET.toLowerCase() + '.aiReferenceLeader'
            });
        }
    }

    setName(name: string): void {
        this.name = name;
    }

    toApiModel(): OutletApiModel {
        return {
            // OutletApiModel
            name: this.name,
            mediaTypes: this.mediaTypes?.map(mt => <LocalizedEnumApiModel>mt),
            circulation: this.circulations?.map(c => <CirculationApiModel>c),
            productType: <LocalizedEnumApiModel>this.productType,
            productFrequency: <LocalizedEnumApiModel>this.productFrequency,
            marketSector: <LocalizedEnumApiModel>this.marketSector,
            targetAudiences: this.targetAudiences?.map(ta => <LocalizedEnumApiModel>ta),
            mediaMarkets: this.mediaMarkets?.map(mediaMarket => mediaMarket.toApiModel()),
            domainAuthority: this.domainAuthority,
            // EntityApiModel
            creationDate: this.creationDate?.toString(),
            handles: this.handles?.map(handle => handle.toApiModel()),
            subjects: SubjectUtils.convertParentSubjectArrayToApi(this.subjects),
            coverageFocus: <LocalizedEnumApiModel>this.coverageFocus,
            areasOfCoverage: this.targetAudienceRegions?.map(tar => tar.toApiEntity()),
            countryOfOrigin: this.countryOfOrigin?.toApi(),
            circulationTier: this.circulationTier,
            primaryLanguage: this.primaryLanguage,
            languages: this.languages?.map(language => language.apiModel ?? language.id),
            bio: this.bio,
            addressLine1: this.addressLine1,
            addressLine2: this.addressLine2,
            postalCode: this.postalCode,
            researchNotes: this.researchNotes,
            isGeoRestricted: this.isGeoRestricted,
            isAiReference: this.isAiReference,
            // JournodexEntityModel
            id: this.id,
            geography: this.geography?.toApiEntity(),
            avatarUrl: this.avatarUrl,
            liveness: this.liveness
        };
    }

    clone(): OutletModel {
        const outlet = new OutletModel(this.toApiModel());
        outlet.geography = this.geography?.clone();
        outlet.subjects = this.subjects.slice();
        return outlet;
    }
}
